import React, { useRef, useState } from 'react';

import { CardCvvElement, Elements, RecurlyProvider, useRecurly } from '@recurly/react-recurly';

import { Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, FormHelperText, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import { Icon, Translate } from '@hydra/common/components';
import { useRecurlyJs } from '@hydra/common/customHooks';
import settings from '@hydra/utils/settings';

const recurlyPublicKey: string = settings.get('recurlyPublicKey');

const useStyles = makeStyles(theme => ({
  cvvLabel: {
    display: 'block',
    marginBottom: theme.spacing(0.5),
    color: theme.palette.text.secondary
  },
  cvvForm: {
    marginBottom: theme.spacing(2)
  },
  cvvElement: {
    width: '100%',
    maxWidth: 160,
    border: `1px solid ${theme.palette.grey[400]}`,
    borderRadius: theme.shape.borderRadius,
    '& > div': {
      height: '40px'
    },
    '&:focus-within': {
      borderColor: theme.palette.primary.main,
      borderWidth: 2
    }
  },
  loadingSpinner: {
    marginRight: theme.spacing(1)
  }
}));

interface CvvVerificationDialogProps {
  isOpen: boolean
  titleContent: string
  descriptionContent: string
  descriptionWith?: { [key: string]: string | number }
  onClose: () => void
  onToken: (recurlyToken: string) => void
}

function CvvForm ({ descriptionContent, descriptionWith, onToken, onClose }: { descriptionContent: string, descriptionWith?: { [key: string]: string | number }, onToken: (recurlyToken: string) => void, onClose: () => void }) {
  const classes = useStyles();
  const recurly = useRecurly();
  const formRef = useRef<HTMLFormElement>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(false);

  const handleConfirm = () => {
    setSubmitting(true);
    setError(false);
    recurly.token(formRef.current!, (recurlyError, token) => {
      if (recurlyError) {
        setSubmitting(false);
        setError(true);
        return;
      }
      onToken(token.id);
    });
  };

  return (
    <>
      <DialogContent>
        <Translate content={descriptionContent} with={descriptionWith} />
        <Typography component='label' variant='body2' className={classes.cvvLabel}>
          <Translate content='cvv' />
        </Typography>
        <FormControl component='form' ref={formRef} className={classes.cvvForm}>
          <CardCvvElement className={classes.cvvElement} />
        </FormControl>
        {error && <FormHelperText error><Translate content='error_500' /></FormHelperText>}
      </DialogContent>
      <DialogActions>
        <Button color='primary' onClick={onClose} disabled={submitting}>
          <Translate content='cancel' />
        </Button>
        <Button variant='contained' color='primary' onClick={handleConfirm} disabled={submitting}>
          {submitting && <Icon type='loader' size='sm' className={classes.loadingSpinner} />}
          <Translate content='confirm' />
        </Button>
      </DialogActions>
    </>
  );
}

export default function CvvVerificationDialog ({ isOpen, titleContent, descriptionContent, descriptionWith, onClose, onToken }: CvvVerificationDialogProps) {
  const recurlyLoaded = useRecurlyJs();

  return (
    <Dialog fullWidth open={isOpen} onClose={onClose}>
      <DialogTitle disableTypography>
        <Typography component='h2' variant='h3' align='center'>
          <Translate content={titleContent} />
        </Typography>
      </DialogTitle>
      {!recurlyLoaded
        ? <DialogContent><CircularProgress /></DialogContent>
        : (
          <RecurlyProvider publicKey={recurlyPublicKey}>
            <Elements>
              <CvvForm descriptionContent={descriptionContent} descriptionWith={descriptionWith} onToken={onToken} onClose={onClose} />
            </Elements>
          </RecurlyProvider>
          )}
    </Dialog>
  );
}
